/**
 * Маска поля: строка в синтаксисе react-input-mask (`9` — цифра, `a` — латинская буква, `*` — буква или цифра,
 * `\` экранирует следующий символ, остальное — постоянные символы) или массив: строка — постоянный символ, RegExp —
 * позиция ввода.
 */
export type InputMaskPattern = string | ReadonlyArray<string | RegExp>;

export type InputMaskSlots = Array<string | RegExp>;

const TOKENS: Record<string, RegExp> = {
    '9': /[0-9]/,
    a: /[A-Za-z]/,
    '*': /[A-Za-z0-9]/,
};

/**
 * Маска в виде массива для `useMask`: в строковом синтаксисе `useMask` токенами считаются ещё `A`, `#` и `?`, а
 * в масках из конфигураций это постоянные символы.
 */
export const parseInputMask = (mask: InputMaskPattern): InputMaskSlots => {
    if (typeof mask !== 'string') return [...mask];
    const slots: InputMaskSlots = [];
    for (let i = 0; i < mask.length; i++) {
        const char = mask[i];
        if (char === '\\' && i + 1 < mask.length) {
            slots.push(mask[++i]);
        } else {
            slots.push(TOKENS[char] ?? char);
        }
    }
    return slots;
};

/** Число постоянных символов до первой позиции ввода (`+7 ` у телефона) */
export const literalPrefixLength = (slots: InputMaskSlots): number => {
    const index = slots.findIndex((slot) => slot instanceof RegExp);
    return index === -1 ? slots.length : index;
};

const significant = (text: string) => text.replace(/[^\p{L}\p{N}+]/gu, '');

/**
 * Начинается ли текст с постоянных символов маски до первой позиции ввода. Да — если в тексте есть сам префикс
 * (`+7` у `+7 999 999-99-99`, разделители не в счёт) или символов для позиций ввода больше, чем позиций
 * (`79123456789`). Иначе `7012345678` потерял бы первую `7` в префиксе `+7`.
 */
export const absorbsLiteralPrefix = (text: string, slots: InputMaskSlots): boolean => {
    const prefix = significant(slots.slice(0, literalPrefixLength(slots)).join(''));
    if (significant(text).startsWith(prefix)) return true;
    const positions = slots.filter((slot): slot is RegExp => slot instanceof RegExp);
    const fitting = [...text].filter((char) => positions.some((slot) => slot.test(char))).length;
    return fitting > positions.length;
};

/**
 * Текст по маске. Постоянный символ маски, совпавший с очередным символом текста, этот символ поглощает: `+79123456789`
 * и `+7 912 345-67-89` в маске `+7 999 999-99-99` дают одно и то же; постоянные символы до первой позиции ввода —
 * только при `absorbPrefix`. Символы, не подходящие к позиции, пропускаются. Без единого символа ввода — пустая строка.
 */
export const conformToMask = (
    text: string,
    slots: InputMaskSlots,
    absorbPrefix = absorbsLiteralPrefix(text, slots),
): string => {
    let result = '';
    let filled = 0;
    let index = 0;
    for (const slot of slots) {
        if (typeof slot === 'string') {
            result += slot;
            if (text[index] === slot && (filled > 0 || absorbPrefix)) index++;
            continue;
        }
        while (index < text.length && !slot.test(text[index])) index++;
        if (index >= text.length) break;
        result += text[index++];
        filled++;
    }
    return filled ? result : '';
};
